<template>
	<div>
		<!-- Interrogations de la banque en cours. L'écran ne les attend plus pour s'afficher : chaque
		     compte dit ici où il en est, et la liste se recharge quand du nouveau est arrivé. -->
		<div v-if="syncing.length" class="bank-flow-sync">
			<div v-for="account in syncing" :key="account.bridgeaccount_id" class="bank-flow-sync__account">
				<font-awesome-icon v-if="account.pending" :icon="['fal', 'spinner']" spin />
				<font-awesome-icon v-else class="text-success" :icon="['fal', 'check']" />
				{{ account.bridgeaccount_label }}
				<span v-if="!account.pending && account.new_transactions" class="bank-flow-sync__new">
					{{ $t('invoice.bank_flow_sync_new', { count: account.new_transactions }) }}
				</span>
			</div>
		</div>

		<div class="form-group">
			<label for="bridgeaccount_id">{{ $t('invoice.bank_accounts') }}</label>
			<e-select
                v-model="bridges_accounts_selected"
                id="bridgeaccount_id"
                track-by="bridgeaccount_id"
                label="bridgeaccount_label"
                :placeholder="$t('check.selectionner_comptes')"
                :selectedLabel="$t('global.selected_label')"
                :options="bridge_accounts"
                :searchable="true"
                :loading="loading_accounts"
                :show-labels="false"
                group-values="accounts"
                group-label="accountingplan_label"
                :group-select="false"
                :multiple="true"
            >
                <template slot="noOptions">{{ $t('global.list_empty') }}</template>
            </e-select>
		</div>

		<DateRangeV2
            :start.sync="start_date"
            :end.sync="end_date"
            @submit="loadTransactions()"
            :ShowTodayButton="true"
            :dateSelect="true"
            :periodToShow="['semaine_derniere','jour','mois','mois_dernier','personalise']"
        />

        <CustomTable
            id_table="bridge_transactions"
            ref="bridge_transactions"
            :items="bridge_transactions"
            :busy.sync="table_busy"
            primaryKey="bridgetransaction_id"
            :hide_if_empty="true"
            :externSlotColumns="extern_slot_columns"
        >
			<template v-slot:[`custom-slot-cell(account_name)`]="{ data }">
				{{ data.bridge_account.bridge_bank.bridgebank_name }} - {{ data.bridge_account.bridgeaccount_name }}<br>
				{{ data.bridge_account.bridgeaccount_iban }}
			</template>
			<template v-slot:[`custom-slot-cell(invoices)`]="{ data }">
				<div v-for="(invoice, key) in data.invoices" :key="key">
					<template v-if="invoice.current_account_id">{{ $t('payment.compte_courant') }}</template>
					<!-- Une remise se nomme par sa référence : ses factures sont trop nombreuses pour
					     tenir dans une cellule, et aucune ne la résume. -->
					<template v-else-if="invoice.checkdeposit_ref">{{ invoice.checkdeposit_ref }}</template>
					<a v-else href="" class="" @click.prevent="quickPreview(invoice)">
					    {{ invoice.num }} <font-awesome-icon :icon="['fas', 'eye']" />
	                </a>
				</div>
			</template>
			<!-- Surligner une partie du libellé l'emporte dans la recherche du rapprochement : c'est là
			     qu'apparaît le nom du commerçant, et l'utilisateur le lit avant de cliquer. -->
			<template v-slot:[`custom-slot-cell(bridgetransaction_description)`]="{ data }">
				<span @mouseup="captureSelection(data)">{{ data.bridgetransaction_description }}</span>
			</template>
			<template v-slot:[`custom-slot-cell(bridgetransaction_amount)`]="{ data }">
				<!-- Le montant est le point d'entrée de la ligne : c'est par lui que l'utilisateur la
				     reconnaît, et l'envoyer au bouton d'action lui demandait de viser ailleurs que là où
				     il regardait. Il mène au rapprochement tant que le mouvement est libre, à l'écriture
				     qui le porte une fois rattaché. -->
				<router-link :to="transactionRoute(data)">
					<PriceFormat
						:class="data.style"
	                    :pathArr="data"
	                    :price="data.bridgetransaction_amount"
	                    currency="bridgetransaction_currency"
	                    :symbol="true"
	                />
				</router-link>
			</template>
			<template v-slot:[`custom-slot-cell(tiers.tiers_rs)`]="{ data }">
				<router-link v-if="data.tiers" :to="{ name: 'tiersFiche', params: { tiers_id: data.tiers.tiers_id }}"><tiers-moral-icon :moral="data.tiers.tiers_moral" />{{ data.tiers.tiers_rs }}</router-link>
			</template>
    	</CustomTable>

        <b-modal size="xl" ref="modelPreview" hide-footer @hidden="pdf_url = null; base64 = null">
            <template v-slot:modal-title>
                {{ $t("action.previsualiser") }}
            </template>

            <iframe v-if="pdf_url != null" :src="pdf_url" height="1000px" width="100%" style="position:relative"></iframe>
            <iframe v-else-if="base64 != null" :src="'data:application/pdf;base64,' + base64" height="1000px" width="100%" style="position:relative"></iframe>
            <div v-else>
                <LoadingSpinner />
            </div>
        </b-modal>
	</div>
</template>

<style scoped>
	.red {
		background: orangered;
		color: white;
		padding: 5px;
	}
	.green {
		background: limegreen;
		color: white;
		padding: 5px;
	}
</style>

<script type="text/javascript">
    import OpenBank from "@/mixins/OpenBank.js"
    import Navigation from "@/mixins/Navigation.js"
    import Payment from "@/mixins/Payment.js"
    import Invoice from "@/mixins/Invoice.js"

	// Temps de lecture laissé aux coches de fin d'interrogation avant qu'elles ne s'effacent.
	const SYNC_NOTICE_DELAY = 3000

	export default {
		name: 'BankFlowList',
		mixins: [OpenBank, Navigation, Payment, Invoice],
		data () {
			return {
				extern_slot_columns: ['account_name', 'invoices', 'tiers.tiers_rs', 'bridgetransaction_amount', 'bridgetransaction_description'],
                start_date: new Date(new Date().setDate(new Date().getDate() - 7)),
                end_date: new Date(),
				bridge_transactions: [],
				table_busy: false,
				events_tab: {
                    'TableAction::goToAssignTransaction': this.openAssignation,
                },
                loading_accounts: false,
                bridges_accounts_selected: [],
                bridge_accounts: [],
				pdf_url: null,
				base64: null,
				// Extrait du libellé surligné à la souris, et le mouvement d'où il vient.
				selected_label: null,
				// Comptes dont on interroge la banque, avec leur avancement.
				syncing: [],
				sync_timer: null
			}
		},

		mounted() {
			this.init_component()
		},

		beforeDestroy() {
			clearTimeout(this.sync_timer)
		},

		methods: {
			async init_component() {
				this.loading_accounts = true
				this.bridge_accounts = await this.getBridgeAccountsAccountingPlan()
				this.loading_accounts = false

				// La période vient de l'adresse quand elle y figure : c'est par elle que le retour d'un
				// rapprochement la restitue. Les comptes, eux, restent mémorisés d'une visite à l'autre,
				// ce qui ne vaudrait pas pour une période, périmée dès le lendemain.
				if(this.$route.query.start && this.$route.query.end) {
					this.start_date = new Date(this.$route.query.start)
					this.end_date = new Date(this.$route.query.end)
				}

				const cache = this.getConfig('bridge_accounts_flow')
				if(cache) {
					const accounts = this.bridge_accounts.reduce((s, b) => s.concat(b.accounts), [])
					this.bridges_accounts_selected = accounts.filter(account => cache.includes(account.bridgeaccount_id))
				}

				// La période est déjà choisie en arrivant : rien ne justifie d'attendre un clic pour
				// montrer ce que la base sait déjà.
				this.loadTransactions()
			},

			/**
			 * Charge les mouvements de la période depuis la base.
			 *
			 * @param {Boolean} sync Interroger ensuite la banque pour les comptes qui en ont besoin.
			 *                       Faux au rechargement qui suit une interrogation : elle vient d'avoir
			 *                       lieu, et s'enchaîner à elle-même ne finirait jamais sur une période
			 *                       qui touche au jour même.
			 */
			async loadTransactions(sync = true) {
				this.table_busy = true

				// L'avancement affiché est celui de la recherche en cours : les coches de la précédente
				// ne disent plus rien de la période qu'on demande.
				if(sync) {
					clearTimeout(this.sync_timer)
					this.syncing = []
				}

				const accounts_ids = this.bridges_accounts_selected.map(account => account.bridgeaccount_id)
				if(accounts_ids.length == 0) {
					this.table_busy = false
					return false
				}

				this.setConfig('bridge_accounts_flow', accounts_ids)

				// La période est portée par l'adresse : la liste se recharge et se partage telle qu'on
				// l'a laissée, et le rapprochement la reprend pour y revenir.
				const start = this.start_date.toDateInputValue()
				const end = this.end_date.toDateInputValue()
				this.$router.replace({ query: { ...this.$route.query, start, end } }).catch(() => {})

				const bridge_transactions = await this.getBridgeTransaction(start, end, accounts_ids)
				this.bridge_transactions = bridge_transactions.map(transaction => ({...transaction, style: transaction.bridgetransaction_amount > 0 ? 'green' : 'red'}))
				this.table_busy = false

				if(sync) {
					this.syncBridge()
				}
			},

			/**
			 * Interroge la banque pour les comptes qui en ont besoin, sans faire attendre la liste.
			 *
			 * Un compte n'est interrogé que s'il n'a rien à montrer sur la période — la base ne connaît
			 * alors peut-être pas encore ses mouvements — ou si la période touche au jour même, où de
			 * nouvelles opérations tombent en permanence. Une période passée déjà peuplée est acquise :
			 * la réinterroger coûtait un appel à chaque affichage pour ne rien rapporter.
			 */
			async syncBridge() {
				const today = new Date().toDateInputValue()
				const covers_today = this.start_date.toDateInputValue() <= today && this.end_date.toDateInputValue() >= today

				const accounts = this.bridges_accounts_selected.filter(account => {
					return covers_today || !this.bridge_transactions.some(transaction => transaction.bridgetransaction_account == account.bridgeaccount_id)
				})

				if(accounts.length == 0) {
					this.syncing = []
					return
				}

				this.syncing = accounts.map(account => ({
					bridgeaccount_id: account.bridgeaccount_id,
					bridgeaccount_label: account.bridgeaccount_label,
					pending: true,
					new_transactions: 0
				}))

				const start = this.start_date.toDateInputValue()
				const end = this.end_date.toDateInputValue()

				await Promise.all(this.syncing.map(async (account) => {
					const result = await this.syncBridgeAccountTransactions(account.bridgeaccount_id, start, end)

					account.new_transactions = result ? result.new_transactions : 0
					account.pending = false
				}))

				// Un seul rechargement en fin de course : recharger à chaque compte ferait sauter la
				// liste sous les yeux de l'utilisateur autant de fois qu'il a de banques.
				if(this.syncing.some(account => account.new_transactions > 0)) {
					this.loadTransactions(false)
				}

				// Les coches ont dit ce qu'elles avaient à dire. Elles s'effacent ensuite : l'écran sert
				// à lire des flux, pas à garder l'avancement d'une interrogation terminée.
				this.sync_timer = setTimeout(() => {
					this.syncing = []
				}, SYNC_NOTICE_DELAY)
			},
			reload() {
				this.$refs.bridge_transactions.unselectAll()
				this.loadTransactions()
			},

			/**
			 * Adresse du mouvement. C'est cette page qui décide de la suite — rapprochement ou renvoi
			 * vers l'écriture déjà posée — pour que l'arrivée par un lien collé mène au même endroit
			 * que le clic depuis la liste.
			 *
			 * Elle est rendue plutôt qu'empruntée à un clic : le montant porte ainsi une vraie adresse,
			 * que le navigateur sait ouvrir dans un onglet à côté sans faire quitter la liste des flux.
			 *
			 * @param {Object} transaction Ligne de flux.
			 * @return {Object} Destination à donner au routeur.
			 */
			transactionRoute(transaction) {
				// Le surlignage ne vaut que pour la ligne où il a été fait : surligner un libellé puis
				// ouvrir le mouvement d'à côté chercherait sur le nom d'un autre commerçant.
				const selected = this.selected_label && this.selected_label.transaction_id === transaction.bridgetransaction_id

				// La période de la liste accompagne le mouvement, pour lui être rendue au retour.
				const query = { start: this.$route.query.start, end: this.$route.query.end }
				if(selected) {
					query.q = this.selected_label.text
				}

				return {
					name: 'bankFlowTransaction',
					params: { transaction_id: transaction.bridgetransaction_id.toString() },
					query
				}
			},

			/**
			 * Ouvre le mouvement depuis le bouton d'action du tableau, qui n'a pas de lien à offrir.
			 *
			 * @param {Object} transaction Ligne de flux sélectionnée.
			 */
			openAssignation(transaction) {
				this.$router.push(this.transactionRoute(transaction))
			},

			/**
			 * Retient ce que l'utilisateur surligne dans un libellé de mouvement, pour l'emporter dans
			 * la recherche du rapprochement. Une sélection vide — un simple clic — n'efface pas la
			 * précédente : elle ne dit rien de plus que le clic qui suit.
			 *
			 * @param {Object} transaction Mouvement dont le libellé porte la sélection.
			 */
			captureSelection(transaction) {
				const selection = window.getSelection().toString().trim()

				if(selection) {
					this.selected_label = { transaction_id: transaction.bridgetransaction_id, text: selection }
				}
			},

            async quickPreview(invoice) {
            	this.pdf_url = null
            	this.base64 = null
            	
                this.$refs.modelPreview.show()
            	if(invoice.pdf_url) {
            		this.pdf_url = invoice.pdf_url
            	}
            	else if(invoice.checkdeposit_id) {
            		let checkdeposit_ids = {checkdeposit_ids: invoice.checkdeposit_id.toString()}
            		let retour = await this.printCheckDeposit(checkdeposit_ids, true)
            		if(retour) {
	                    this.base64 = retour
	                }
            	}
            	else {
	                let retour = await this.pdfInvoice([invoice.id], "", true)
	                if(retour) {
	                    this.base64 = retour
	                }
            	}
            }
		},

		components: {
            CustomTable: () => import('GroomyRoot/components/Table/CustomTable'),
            DateRangeV2: () => import('GroomyRoot/components/Inputs/DateRangeV2'),
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
        	PriceFormat: () => import('GroomyRoot/components/Format/PriceFormat')
		}
	}

</script>